import { PrimaryDefaultButton } from '@/components/buttons/primary';
import SecondaryDefaultButton from '@/components/buttons/secondary';
import GroupedInput from '@/components/forms/GroupedInput';
import { Container as ContainerComponent } from '@/components/layout/Container';
import Modal from '@/components/modals/Modal';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import styled, { css } from 'styled-components';

/*
 * Adding $ to boolean props to make them transient styled-components props
 * Otherwise we get a console error for unrecognized boolean props on a DOM element
 * See https://styled-components.com/docs/api#transient-props
 */

interface HeroProps {
  $background?: string;
  $fade: boolean;
  $inverted: boolean;
}

const Hero = styled.div<HeroProps>`
  --padding-y: ${remCalc(60)};

  position: relative;
  margin-top: ${`-${PRIMARY_NAV_HEIGHT}px`};
  padding-top: ${`calc(${PRIMARY_NAV_HEIGHT}px + var(--padding-y))`};
  padding-bottom: ${(props) => (props.$fade ? 0 : `var(--padding-y)`)};
  color: ${(props) => (props.$inverted ? COLORS.WHITE : COLORS.BLACK)};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${(props) => (props.$fade ? `calc(100% + var(--padding-y))` : `100%`)};
    z-index: -1;

    background-image: ${(props) => props.$background ?? 'transparent'};
    background-position: center top;
    background-size: cover;
    background-repeat: no-repeat;
  }

  ${(props) =>
    props.$inverted &&
    css`
  ${PrimaryDefaultButton} {
    --button-color: ${COLORS.WHITE};
    --button-text-color: ${COLORS.VIOLET.LOWLIGHT};
    --button-color-hover: ${COLORS.VIOLET.HIGHLIGHT};
    --button-text-color-hover: ${COLORS.VIOLET.LOWLIGHT};
    --button-border-color-hover: transparent;
  }

  ${SecondaryDefaultButton} {
    --button-color: transparent;
    --button-text-color: ${COLORS.VIOLET.TINT};
    border-color: ${COLORS.VIOLET.TINT};
    --button-color-hover: ${COLORS.VIOLET.XTRALIGHT};
    --button-text-color-hover: ${COLORS.VIOLET.LOWLIGHT};
  }
    `}

  ${mediaBreakpointUp('desktop')} {
    --padding-y: ${remCalc(80)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    --padding-y: ${remCalc(100)};
  }
`;

const Container = styled(ContainerComponent)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: auto;
  gap: ${remCalc(30)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(18, 1fr);
    column-gap: 20px;
  }
`;

const Eyebrow = styled.p`
  ${customTextSize(14, 18)}
  font-weight: ${FONT_WEIGHTS.BOLD};
  text-align: inherit;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: -25px auto 30px;
  display: block;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 20)}
  }
`;

const Heading = styled.header`
  margin-bottom: 30px;

  h1 {
    ${customTextSize(32, 38)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    letter-spacing: -0.01em;
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / -1;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 3 / -3;

    h1 {
      ${customTextSize(49, 58)}
    }
  }
`;

const TextContainer = styled.div`
  ${customTextSize(16, 24)}

  > * + * {
    margin-top: 15px;
  }

  a {
    text-decoration: underline;
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / -1;
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)}
    grid-column: 2 / -2;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 4 / -4;
  }
`;

const StyledGroupedInput = styled(GroupedInput)`
  width: 100%;
  max-width: 500px;

  --grouped-input-border-color: ${COLORS.BLUE.LOWLIGHT};
  --input-border-color-hover: ${COLORS.BLUE.LOWLIGHT};

  ${mediaBreakpointUp('tablet')} {
    grid-column: 2 / -2;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 5 / -5;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 7 / -7;
    width: 100%;
  }
`;

const LeftColumn = styled.div<{ $hasAsset: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  grid-column: 1 / -1;

  ${mediaBreakpointUp('desktop')} {
    ${(props) =>
    props.$hasAsset &&
    css`
        grid-column: 1 / span 9;
        text-align: left;
        align-items: flex-start;

        ${CTAWrapper} {
          justify-content: flex-start;
        }
      `}
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  gap: ${remCalc(20)};
  margin-top: 40px;

  ${mediaBreakpointUp('tablet')} {
    margin-top: 30px;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const RightColumn = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;

  video {
    border-radius: 10px;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 10 / span 9;
  }
`;

const AssetWrapper = styled.div`
  border-radius: ${remCalc(10)};

  > * {
    width: 100%;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  margin: auto 0;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: ${COLORS.WHITE};

  > svg {
    opacity: 0.95;
  }

  video {
    width: 100%;
    height: 100%;
  }

  ${mediaBreakpointUp('desktop')} {
    margin-left: 2rem;
  }
`;

const ModalVideoWrapper = styled.div`
  border-radius: 10px;
  overflow: hidden;
  background: ${COLORS.WHITE};

  video {
    width: 100%;
    height: 100%;
  }
`;

const VideoModal = styled(Modal)`
  --modal-padding: 0;
  --modal-max-width: 1400px;
  --modal-button-top: -2.5rem;
  --modal-button-right: 0;
`;

const simpleHeroComponents = {
  AssetWrapper,
  CTAWrapper,
  Container,
  Eyebrow,
  Heading,
  Hero,
  LeftColumn,
  ModalVideoWrapper,
  RightColumn,
  StyledGroupedInput,
  TextContainer,
  VideoModal,
  VideoWrapper,
};

export default simpleHeroComponents;
